/* eslint-disable react/no-array-index-key */
import clsx from "clsx";
import { Children, ReactNode } from "react";

import { Pane } from "./pane";
import { StageOverlay } from "./stage_overlay";

import { CenteredErrorBoundary } from "components/error_boundary";
import { useAppSelector } from "hooks/redux_hooks";
import { useIsMobile } from "hooks/use_is_mobile";
import { useIsWaitingForClassToStart } from "hooks/use_is_waiting_for_class_to_start";
import { PaneContentType, PaneType } from "shared/types";
import { classroomSelectors } from "store";
import { getVisiblePanes } from "utils/stage";

import "./stage.scss";

function Panes({
  isPresentationView,
  children,
}: {
  isPresentationView: boolean;
  children: ReactNode;
}) {
  const count = Children.toArray(children).length;

  return (
    <div
      className={clsx(`stage stage-${count}-up flex-auto`, {
        "presentation-view": isPresentationView,
      })}
    >
      {children}
    </div>
  );
}

const isSmallPane = (panes: PaneType[], index: number) => {
  if (panes.length === 6 || panes.length === 8) return true;
  if (panes.length === 5 || panes.length === 7) return index > 0;
  return false;
};

function Stage({
  panes,
  canControlClassroom,
}: {
  panes: PaneType[];
  canControlClassroom?: boolean;
}) {
  const isInPerson = useAppSelector(classroomSelectors.selectIsInPersonClass());
  const activeBreakout = useAppSelector(classroomSelectors.selectActiveBreakoutSessionId());
  const isMobile = useIsMobile();
  const isWaitingToStart = useIsWaitingForClassToStart();
  const isPresentationView = useAppSelector(classroomSelectors.selectIsPresentationView());
  const hasPresentationView = useAppSelector(classroomSelectors.selectHasPresentationView());
  const isTimelineEmpty = useAppSelector(classroomSelectors.selectTimelineIsEmpty());
  const visiblePanes = getVisiblePanes(
    panes,
    isInPerson,
    isPresentationView,
    isMobile,
    canControlClassroom || false,
    !!activeBreakout,
  );
  const noContentToDisplay = visiblePanes.length === 0;

  return (
    <>
      <Panes isPresentationView={isPresentationView}>
        {visiblePanes.map((pane, index) => (
          <CenteredErrorBoundary
            key={`pane-${pane.content_type !== PaneContentType.Empty ? pane.content_type : index}-${
              pane.content_id
            }`}
            text="This content cannot be displayed"
          >
            <Pane
              contentId={pane.content_id}
              contentType={pane.content_type}
              featuredPollResponse={pane.featured_poll_response}
              showingHiddenNote={
                hasPresentationView &&
                pane.hidden_in_presentation_view &&
                canControlClassroom &&
                !isPresentationView
              }
              index={index}
              isSmallPane={isSmallPane(visiblePanes, index)}
              isPinned={pane.is_pinned}
            />
          </CenteredErrorBoundary>
        ))}
      </Panes>
      {!isTimelineEmpty && (isWaitingToStart || noContentToDisplay) && (
        <StageOverlay
          isInstructor={canControlClassroom || false}
          isMobile={isMobile}
          isInPerson={isInPerson}
          isWaitingToStart={isWaitingToStart}
          isPresentationView={isPresentationView}
          noContentToDisplay={noContentToDisplay}
        />
      )}
    </>
  );
}

export { Panes, Stage };
